import { FC, useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import Editor, { loader, type Monaco } from '@monaco-editor/react';
import { Button, Col, Divider, Row, Space, Tag, Typography } from 'antd';
import type * as MonacoEditor from 'monaco-editor';
import { createPortal } from 'react-dom';
import { TbAlignLeft, TbBulb, TbDeviceFloppy, TbPlayerPlay } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { MiddleDot } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import { SetStateMethod } from '@/types';

import type {
    VantageEditorPreferences,
    VantageMetadata,
    VantageQueryResult,
    VantageValidationResult,
    VantageVisualizationType,
} from '../../../types';
import { VANTAGE_SQL_PLACEHOLDER } from '../../../types';
import VantageResultView from '../../../VantageResultView';
import { registerVantageCompletionProvider } from './completionProvider';
import VantageExportDropdown from './components/VantageExportDropdown';
import { formatSuggestWidgetDom, SUGGEST_STATUS_BAR_HEIGHT } from './suggestWidgetDom';
import {
    defineVantageMonacoTheme,
    toMonacoHexColor,
    VANTAGE_MONACO_DARK_THEME,
    VANTAGE_MONACO_LIGHT_THEME,
} from './theme';

loader.init();

interface QueryEditorProps {
    editorRef: MutableRefObject<MonacoEditor.editor.IStandaloneCodeEditor | null>;
    code: string;
    setCode: SetStateMethod<string>;
    visualization: VantageVisualizationType;
    preferences: VantageEditorPreferences;
    metadata: VantageMetadata;
    metadataError: string | null;
    validation: VantageValidationResult | null;
    validating: boolean;
    result: VantageQueryResult | null;
    error: string | null;
    runtimeMs: number | null;
    running: boolean;
    saving: boolean;
    canRunOrSave: boolean;
    runQuery: () => void;
    saveConfiguration: () => void;
    formatQuery: () => void;
}

const QueryEditor: FC<QueryEditorProps> = ({
    editorRef,
    code,
    setCode,
    visualization,
    preferences,
    metadata,
    metadataError,
    validation,
    validating,
    result,
    error,
    runtimeMs,
    running,
    saving,
    canRunOrSave,
    runQuery,
    saveConfiguration,
    formatQuery,
}): JSX.Element => {
    const { token, resolvedThemeName } = useAntDesignTheme();
    const monacoRef = useRef<Monaco | null>(null);
    const metadataRef = useRef<VantageMetadata>({ tables: [], variables: [] });
    const completionProviderDisposableRef = useRef<MonacoEditor.IDisposable | null>(null);
    const [suggestEmptyViewTarget, setSuggestEmptyViewTarget] = useState<HTMLElement | null>(null);
    const isDarkMode = resolvedThemeName === 'dark';
    const editorTheme = isDarkMode ? VANTAGE_MONACO_DARK_THEME : VANTAGE_MONACO_LIGHT_THEME;
    const monacoThemeColors = useMemo(() => {
        const fallbackBackground = isDarkMode ? '#141414' : '#ffffff';
        const background = toMonacoHexColor(token.colorBgElevated, fallbackBackground);

        return {
            background,
            border: toMonacoHexColor(token.colorBorder, isDarkMode ? '#303030' : '#d9d9d9', background),
            hoverBackground: toMonacoHexColor(token.colorFillTertiary, isDarkMode ? '#1f1f1f' : '#f5f5f5', background),
            lineHighlightBackground: toMonacoHexColor(
                token.colorFillQuaternary,
                isDarkMode ? '#1f1f1f' : '#fafafa',
                background,
            ),
            primary: toMonacoHexColor(token.colorPrimary, '#1677ff', background),
            text: toMonacoHexColor(token.colorText, isDarkMode ? '#f5f5f5' : '#1f1f1f', background),
            textSecondary: toMonacoHexColor(token.colorTextSecondary, isDarkMode ? '#d9d9d9' : '#595959', background),
            textTertiary: toMonacoHexColor(token.colorTextTertiary, '#8c8c8c', background),
        };
    }, [
        isDarkMode,
        token.colorBgElevated,
        token.colorBorder,
        token.colorFillQuaternary,
        token.colorFillTertiary,
        token.colorPrimary,
        token.colorText,
        token.colorTextSecondary,
        token.colorTextTertiary,
    ]);
    const editorFontSize = typeof token.fontSize === 'number' ? token.fontSize : 14;
    const editorLineHeight = Math.max(20, Math.round(editorFontSize * 1.55));
    const suggestFontSize = Math.max(12, editorFontSize);
    const suggestLineHeight = Math.max(68, Math.round(suggestFontSize * 4.85));
    const suggestFooterHeightAdjustment = Math.max(0, suggestLineHeight - SUGGEST_STATUS_BAR_HEIGHT);
    const tagBorderRadius = typeof token.borderRadius === 'number' ? `${token.borderRadius}px` : token.borderRadius;
    const suggestWidgetBorderRadius =
        typeof token.borderRadius === 'number' ? `${Math.max(token.borderRadius, 8)}px` : token.borderRadius;

    const triggerEditorSuggest = useCallback(
        (delay = 0) => {
            const editor = editorRef.current;

            if (!editor || !preferences.autocomplete) {
                return;
            }

            window.setTimeout(() => {
                if (!editor.hasTextFocus()) {
                    return;
                }

                editor.trigger('vantage', 'editor.action.triggerSuggest', {});
            }, delay);
        },
        [editorRef, preferences.autocomplete],
    );

    useEffect(
        () => () => {
            completionProviderDisposableRef.current?.dispose();
        },
        [],
    );

    useEffect(() => {
        let animationFrameId: number | null = null;
        const scheduleFormat = () => {
            if (animationFrameId !== null) {
                return;
            }

            animationFrameId = window.requestAnimationFrame(() => {
                animationFrameId = null;
                const nextEmptyViewTarget = formatSuggestWidgetDom(suggestFooterHeightAdjustment);

                setSuggestEmptyViewTarget((currentTarget) =>
                    currentTarget === nextEmptyViewTarget ? currentTarget : nextEmptyViewTarget,
                );
            });
        };
        const observer = new MutationObserver(scheduleFormat);

        scheduleFormat();
        observer.observe(document.body, { childList: true, characterData: true, subtree: true });

        return () => {
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }

            observer.disconnect();
        };
    }, [suggestFooterHeightAdjustment]);

    useEffect(() => {
        metadataRef.current = metadata;

        if (!preferences.autocomplete || (!metadata.tables.length && !metadata.variables.length)) {
            return;
        }

        triggerEditorSuggest();
    }, [metadata, preferences.autocomplete, triggerEditorSuggest]);

    useEffect(() => {
        const monacoInstance = monacoRef.current;

        if (!monacoInstance) {
            return;
        }

        defineVantageMonacoTheme(monacoInstance, editorTheme, isDarkMode, monacoThemeColors);
        monacoInstance.editor.setTheme(editorTheme);
    }, [editorTheme, isDarkMode, monacoThemeColors]);

    const resultCount = result?.rowCount ?? 0;

    return (
        <div
            className='vantage-query-editor'
            style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}
        >
            <style>
                {`
                    .monaco-editor .suggest-widget {
                        border-radius: ${suggestWidgetBorderRadius};
                        margin-top: 0.25rem;
                        overflow: hidden;
                    }

                    .monaco-editor .suggest-widget.message.vantage-suggest-empty-state {
                        flex-direction: column;
                        height: 10rem !important;
                        min-height: 10rem;
                    }

                    .monaco-editor .suggest-widget.message.vantage-suggest-empty-state > .message {
                        box-sizing: border-box;
                        display: block !important;
                        height: 100%;
                        padding: 0;
                        width: 100%;
                    }

                    .monaco-editor .suggest-widget .vantage-suggest-empty-view {
                        height: 100%;
                        width: 100%;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row {
                        align-items: center;
                        border-bottom: 1px solid ${token.colorBorder};
                        box-sizing: border-box;
                        display: flex;
                        padding: 0.375rem 0.625rem 0.375rem 0.5rem;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row.focused,
                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row.selected {
                        background-color: ${token.colorFillTertiary} !important;
                        color: ${token.colorText} !important;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row.vantage-suggest-section-start {
                        padding-top: 1.875rem;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row .vantage-suggest-section-label {
                        color: ${token.colorTextTertiary};
                        display: block;
                        font-size: 0.6875rem;
                        font-weight: 500;
                        left: 0.5rem;
                        line-height: 1;
                        margin-bottom: 0.375rem;
                        pointer-events: none;
                        position: absolute;
                        top: 0.375rem;
                        z-index: 1;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row:last-child {
                        border-bottom: 0;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row > .contents {
                        height: auto;
                        min-width: 0;
                        padding-left: 0;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row > .contents > .main {
                        align-content: center;
                        align-items: center;
                        column-gap: 0.625rem;
                        display: grid;
                        grid-template-columns: 1.5rem minmax(0, 1fr);
                        grid-template-rows: auto auto;
                        height: 100%;
                        justify-content: normal;
                        line-height: normal !important;
                        row-gap: 0.125rem;
                        white-space: normal;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row .suggest-icon {
                        align-items: center;
                        align-self: center;
                        border: 1px solid ${token.colorBorder};
                        background-color: ${token.colorBgBase};
                        border-radius: 4px;
                        box-sizing: border-box;
                        display: inline-flex;
                        grid-column: 1;
                        grid-row: 1 / span 2;
                        height: 16px;
                        justify-content: center;
                        justify-self: center;
                        margin: 0;
                        width: 16px;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row .suggest-icon.codicon-symbol-variable,
                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row .suggest-icon.codicon-symbol-field {
                        border-color: ${token.blue3};
                        background-color: ${token.blue1};
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row .suggest-icon.codicon-symbol-class {
                        border-color: ${token.gold3};
                        background-color: ${token.gold1};
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row .suggest-icon::before {
                        font-size: 10px;
                        line-height: 1;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row.focused .suggest-icon.codicon-symbol-keyword,
                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row.selected .suggest-icon.codicon-symbol-keyword {
                        color: var(--vscode-symbolIcon-keywordForeground) !important;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row.focused .suggest-icon.codicon-symbol-variable,
                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row.selected .suggest-icon.codicon-symbol-variable {
                        color: var(--vscode-symbolIcon-variableForeground) !important;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row.focused .suggest-icon.codicon-symbol-field,
                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row.selected .suggest-icon.codicon-symbol-field {
                        color: var(--vscode-symbolIcon-fieldForeground) !important;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row.focused .suggest-icon.codicon-symbol-class,
                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row.selected .suggest-icon.codicon-symbol-class {
                        color: var(--vscode-symbolIcon-classForeground) !important;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row.focused .suggest-icon.codicon-symbol-module,
                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row.selected .suggest-icon.codicon-symbol-module {
                        color: var(--vscode-symbolIcon-moduleForeground) !important;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row.focused .suggest-icon.codicon-symbol-snippet,
                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row.selected .suggest-icon.codicon-symbol-snippet {
                        color: var(--vscode-symbolIcon-snippetForeground) !important;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row > .contents > .main > .left {
                        align-items: center;
                        display: flex;
                        grid-column: 2;
                        grid-row: 1;
                        min-width: 0;
                        overflow: hidden;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row > .contents > .main > .right {
                        display: block;
                        grid-column: 2;
                        grid-row: 2;
                        max-width: 100%;
                        min-width: 0;
                        overflow: hidden;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row .monaco-icon-label,
                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row .monaco-icon-label-container,
                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row .monaco-icon-name-container {
                        display: block;
                        min-width: 0;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row .label-name {
                        color: inherit;
                        display: block;
                        font-size: ${suggestFontSize}px;
                        font-weight: 600;
                        line-height: 1.25;
                        overflow: hidden;
                        text-decoration: none;
                        text-overflow: ellipsis;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row .signature-label,
                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row .qualifier-label {
                        display: none;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row .details-label {
                        color: ${monacoThemeColors.textSecondary};
                        display: block !important;
                        font-size: ${Math.max(11, suggestFontSize - 2)}px;
                        line-height: 1.25;
                        margin-left: 0 !important;
                        opacity: 1;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }

                    .monaco-editor .suggest-widget .monaco-list .monaco-list-row.focused .details-label {
                        color: ${monacoThemeColors.textSecondary};
                    }

                    .monaco-editor .suggest-widget .suggest-status-bar {
                        align-items: center;
                        box-sizing: border-box;
                        flex: 0 0 ${SUGGEST_STATUS_BAR_HEIGHT}px !important;
                        height: ${SUGGEST_STATUS_BAR_HEIGHT}px !important;
                        max-height: ${SUGGEST_STATUS_BAR_HEIGHT}px !important;
                        min-height: ${SUGGEST_STATUS_BAR_HEIGHT}px !important;
                        padding: 0.25rem 0.75rem !important;
                    }

                    .monaco-editor .suggest-widget .suggest-status-bar .left,
                    .monaco-editor .suggest-widget .suggest-status-bar .right,
                    .monaco-editor .suggest-widget .suggest-status-bar .actions-container {
                        align-items: center;
                        display: flex;
                    }

                    .monaco-editor .suggest-widget.with-status-bar .suggest-status-bar .action-label {
                        align-items: center;
                        color: ${monacoThemeColors.textSecondary};
                        font-weight: 400;
                        display: inline-flex;
                        line-height: 1;
                    }

                    .monaco-editor .suggest-widget .suggest-status-bar .vantage-suggest-shortcut-tag {
                        background-color: ${token.colorBorder};
                        border-radius: ${tagBorderRadius};
                        color: ${monacoThemeColors.textSecondary};
                        font-size: 0.75rem;
                        font-weight: 400;
                        line-height: 1;
                        margin-left: 0.5rem;
                        padding: 0.125rem 0.25rem;
                        vertical-align: 0.05rem;
                    }
                `}
            </style>
            {suggestEmptyViewTarget
                ? createPortal(
                      <EmptySection
                          description='Try another keyword, table, column, or dynamic variable.'
                          height='100%'
                          icon={<TbBulb size='1.25rem' />}
                          label='No Suggestions Found'
                      />,
                      suggestEmptyViewTarget,
                  )
                : null}
            <Row style={{ margin: '1rem', marginBottom: 0 }} align='middle' wrap={false} gutter={[8, 0]}>
                <Col flex='none'>
                    <small style={{ color: token.colorTextTertiary, verticalAlign: '1px' }}>Vantage Query Editor</small>
                </Col>
                <Col flex='auto' />
                <Col flex='none'>
                    <Button size='small' disabled={!code.trim()} icon={<TbAlignLeft />} onClick={formatQuery}>
                        Format
                    </Button>
                </Col>
            </Row>
            <div
                style={{
                    flex: '1 1 44%',
                    minHeight: 0,
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    overflow: 'hidden',
                    margin: '1rem',
                }}
            >
                <Editor
                    height='100%'
                    language='sql'
                    theme={editorTheme}
                    loading={<LoadingOutlined spin />}
                    value={code}
                    onChange={(val) => setCode(val ?? '')}
                    onMount={(editor, monacoInstance) => {
                        editorRef.current = editor;
                        monacoRef.current = monacoInstance;
                        defineVantageMonacoTheme(monacoInstance, editorTheme, isDarkMode, monacoThemeColors);
                        monacoInstance.editor.setTheme(editorTheme);
                        completionProviderDisposableRef.current?.dispose();
                        completionProviderDisposableRef.current = registerVantageCompletionProvider(
                            monacoInstance,
                            () => metadataRef.current,
                        );

                        editor.onDidFocusEditorText(() => {
                            triggerEditorSuggest();
                        });

                        editor.onDidChangeModelContent((event) => {
                            if (event.isFlush) {
                                return;
                            }

                            triggerEditorSuggest();
                        });

                        triggerEditorSuggest(50);
                    }}
                    options={{
                        fontFamily: token.fontFamily,
                        fontSize: editorFontSize,
                        lineHeight: editorLineHeight,
                        suggestFontSize,
                        suggestLineHeight,
                        minimap: { enabled: false },
                        padding: { top: 8, bottom: 8 },
                        placeholder: VANTAGE_SQL_PLACEHOLDER,
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        fixedOverflowWidgets: true,
                        wordWrap: 'off',
                        lineNumbers: 'on',
                        renderLineHighlight: 'line',
                        suggestOnTriggerCharacters: preferences.autocomplete,
                        quickSuggestionsDelay: 0,
                        tabCompletion: preferences.autocomplete ? 'on' : 'off',
                        acceptSuggestionOnEnter: 'on',
                        snippetSuggestions: 'bottom',
                        wordBasedSuggestions: 'off',
                        suggest: {
                            showStatusBar: true,
                            showIcons: true,
                            showSnippets: true,
                            selectionMode: 'always',
                            filterGraceful: true,
                            snippetsPreventQuickSuggestions: false,
                            preview: false,
                            showInlineDetails: true,
                        },
                        quickSuggestions: {
                            other: preferences.autocomplete,
                            comments: preferences.autocomplete,
                            strings: false,
                        },
                    }}
                />
            </div>

            <div
                style={{
                    borderBottom: `1px solid ${token.colorBorder}`,
                    borderTop: `1px solid ${token.colorBorder}`,
                    padding: '0.75rem 1rem',
                }}
            >
                <Row align='middle' gutter={[8, 8]} wrap>
                    <Col flex='none'>
                        <small style={{ color: token.colorTextTertiary }}>Status</small>
                    </Col>
                    <Col flex='none'>
                        <Tag color={validating ? 'processing' : validation?.executable ? 'success' : 'warning'}>
                            {validating ? 'Validating' : validation?.executable ? 'Executable' : 'Draft'}
                        </Tag>
                    </Col>
                    <Col flex='none'>
                        <Tag>{resultCount.toLocaleString()} rows</Tag>
                    </Col>
                    <Col flex='none'>
                        <Tag>{result?.columns.length ?? 0} columns</Tag>
                    </Col>
                    {validation?.parameterCount !== undefined && (
                        <Col flex='none'>
                            <Tag>{validation.parameterCount} variables</Tag>
                        </Col>
                    )}
                    <Col flex='auto' />
                </Row>
                {(validation?.message && !validation.executable) || metadataError ? (
                    <Typography.Text type='secondary' style={{ display: 'block', marginTop: '0.375rem' }}>
                        {validating ? 'Validating Vantage query…' : validation?.message || metadataError}
                    </Typography.Text>
                ) : null}
            </div>

            <Row style={{ margin: '1rem', marginBottom: 0 }} align='middle' wrap={false}>
                <Col flex='none'>
                    <small style={{ color: token.colorTextTertiary, verticalAlign: '1px' }}>Query Results</small>
                </Col>
                <Col flex='auto' />
            </Row>

            {error && <PlatformAlert type='error' showIcon message={error} style={{ margin: '0.75rem 1rem 0' }} />}

            <div
                style={{
                    flex: '1 1 44%',
                    minHeight: 0,
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    overflow: 'hidden',
                    margin: '1rem',
                }}
            >
                <VantageResultView
                    result={result}
                    visualization={visualization}
                    density={preferences.resultDensity}
                    fitContainer
                />
            </div>

            <Row style={{ margin: '1rem', marginTop: 0 }} wrap={false} align='middle'>
                <Col flex='none'>
                    <small style={{ verticalAlign: '1px', color: token.colorTextTertiary }}>
                        {resultCount.toLocaleString()} Results
                    </small>
                </Col>
                <Col flex='none'>
                    <MiddleDot horizontalMargin='0.5rem' />
                </Col>
                <Col flex='none'>
                    <small style={{ verticalAlign: '1px', color: token.colorTextTertiary }}>
                        {runtimeMs === null ? 'Not run yet' : `${(runtimeMs / 1000).toFixed(2)} seconds`}
                    </small>
                </Col>
                <Col flex='auto' />
                <Col flex='none'>
                    <Space size={8}>
                        <VantageExportDropdown disabled={!canRunOrSave} sql={code} />
                        <Divider type='vertical' style={{ height: '1.25rem', marginInline: 0 }} />
                        <Space.Compact>
                            <Button
                                size='small'
                                loading={saving}
                                disabled={!canRunOrSave || running}
                                onClick={saveConfiguration}
                                icon={<TbDeviceFloppy />}
                            >
                                Save
                            </Button>
                            <Button
                                size='small'
                                type='primary'
                                loading={running}
                                disabled={!canRunOrSave || saving}
                                onClick={runQuery}
                                icon={<TbPlayerPlay />}
                            >
                                Run
                            </Button>
                        </Space.Compact>
                    </Space>
                </Col>
            </Row>
        </div>
    );
};

export default QueryEditor;
